import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { LayerConfig } from '../components/LayerPanel';
import { RealTimePoller } from '../data/RealTimePoller';
import { fetchUSGSEarthquakes, USGS_STRIDE } from '../data/usgsConnector';
import { fetchFIRMSHotspots, FIRMS_STRIDE } from '../data/firmsConnector';
import { parseOpenSkyStates, AircraftBbox } from '../data/openSkyConnector';
import { AircraftInterpolator } from '../data/AircraftInterpolator';
import { TemporalContext } from '../context/TemporalController';

export interface LayerState {
  layers: LayerConfig[];
  onToggle: (id: string, visible: boolean) => void;
  onOpacityChange: (id: string, opacity: number) => void;
}

// ---------- Data source definitions ----------

interface TileSourceDef {
  kind: 'tile';
  id: string;
  label: string;
  name: string;
  urlTemplate: string;
  gridType: 'bing' | 'xyz' | 'gibs';
  maxZoom: number;
  isOverlay: boolean;
  isElevation?: boolean;
  colormapType: number;
  defaultVisible: boolean;
  defaultOpacity: number;
}

const API_BASE = process.env.REACT_APP_API_URL || '';
const CLOUDFRONT_URL = process.env.REACT_APP_CLOUDFRONT_URL || '';

/**
 * Tile sources — registered with C++ SPE via addTileSource.
 * All fetching/rendering done in C++.
 */
const TILE_SOURCES: TileSourceDef[] = [
  {
    kind: 'tile', id: 'elevation', label: 'Elevation', name: 'AWS Terrain',
    urlTemplate: 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
    gridType: 'xyz', maxZoom: 9, isOverlay: false, colormapType: 2,
    defaultVisible: true, defaultOpacity: 1.0, isElevation: true,
  },
  // {
  //   kind: 'tile', id: 'ndvi', label: 'NDVI Vegetation', name: 'MODIS NDVI',
  //   urlTemplate: 'https://gibs.earthdata.nasa.gov/wmts/epsg4326/best/MODIS_Terra_L3_NDVI_16Day/default/{date}/250m/{z}/{y}/{x}.png',
  //   gridType: 'gibs', maxZoom: 7, isOverlay: true, colormapType: 2,
  //   defaultVisible: false, defaultOpacity: 0.8,
  // },
  // {
  //   kind: 'tile', id: 'gbif', label: 'Biodiversity', name: 'GBIF',
  //   urlTemplate: 'https://tile.gbif.org/3857/density/occurrence/{z}/{x}/{y}@1x.png?style=orange.marker&bin=hex&hexPerTile=51',
  //   gridType: 'xyz', maxZoom: 8, isOverlay: true, colormapType: 2,
  //   defaultVisible: false, defaultOpacity: 0.8,
  // },
];
/*
// Conditionally add sources that require environment variables
if (CLOUDFRONT_URL) {
  TILE_SOURCES.push({
    kind: 'tile', id: 'ghsl', label: 'Human Settlement', name: 'GHSL',
    urlTemplate: `${CLOUDFRONT_URL}/ghsl/{z}/{x}/{y}.png`,
    gridType: 'xyz', maxZoom: 8, isOverlay: true, colormapType: 1,
    defaultVisible: false, defaultOpacity: 0.8,
  });
  TILE_SOURCES.push({
    kind: 'tile', id: 'gfs-temp', label: 'Weather (GFS)', name: 'GFS Temperature',
    urlTemplate: `${CLOUDFRONT_URL}/data/gfs/{date}/{z}/{x}/{y}.png`,
    gridType: 'xyz', maxZoom: 4, isOverlay: true, colormapType: 3,
    defaultVisible: false, defaultOpacity: 0.7,
  });
}

if (API_BASE) {
  TILE_SOURCES.push({
    kind: 'tile', id: 'gfw', label: 'Fishing Effort', name: 'GFW',
    urlTemplate: `${API_BASE}/api/geo/gfw?z={z}&x={x}&y={y}&format=PNG&datasets[0]=public-global-fishing-effort:latest&interval=MONTH`,
    gridType: 'xyz', maxZoom: 8, isOverlay: true, colormapType: 2,
    defaultVisible: false, defaultOpacity: 0.8,
  });
}
*/
// ---------- Marker feed layer config ----------

const MARKER_LAYERS: LayerConfig[] = [
  // { id: 'earthquakes', label: 'Earthquakes',  visible: false, opacity: 1.0, loading: false },
  // { id: 'fires',       label: 'Wildfires',    visible: false, opacity: 1.0, loading: false },
  // { id: 'aircraft',    label: 'Aircraft',     visible: false, opacity: 1.0, loading: false },
];

/** Wide default bbox for OpenSky */
const DEFAULT_AIRCRAFT_BBOX: AircraftBbox = {
  lonMin: -180, latMin: -90, lonMax: 180, latMax: 90,
};

const OPENSKY_POLL_MS = 10_000;

/**
 * useLayerManager — hybrid approach:
 *
 * MARKER LAYERS (earthquakes, fires, aircraft):
 *   JS polling via RealTimePoller/AircraftInterpolator → pushMarkerData → overlay pass
 *   This path is proven working.
 *
 * TILE LAYERS (NDVI, GBIF, GHSL, GFS, GFW):
 *   Registered with C++ SPE via addTileSource → resource manager downloads → overlay pass
 *   Visibility/opacity controlled via setTileSourceVisible/Opacity.
 */
export function useLayerManager(): LayerState {
  // Combine marker + tile layers into one config
  const [layers, setLayers] = useState<LayerConfig[]>([
    ...MARKER_LAYERS,
    ...TILE_SOURCES.map(s => ({
      id: s.id, label: s.label, visible: s.defaultVisible,
      opacity: s.defaultOpacity, loading: false,
    })),
  ]);

  const pollerRef = useRef<RealTimePoller | null>(null);
  const interpolatorRef = useRef<AircraftInterpolator | null>(null);
  const openSkyIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tileIndicesRef = useRef<Map<string, number>>(new Map());

  const updateLayer = useCallback((id: string, patch: Partial<LayerConfig>) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  }, []);

  // --- JS marker pipeline (proven working) ---

  const makePushFn = useCallback(() => {
    return (layerId: string, data: Float32Array, count: number) => {
      const r = (window as any)._scRenderer;
      if (r && typeof r.pushMarkerData === 'function') {
        try { r.pushMarkerData(layerId, data, count); } catch {}
      }
      updateLayer(layerId, { loading: false });
    };
  }, [updateLayer]);

  const wrapFetch = useCallback(
    (layerId: string, fetchFn: () => Promise<Float32Array | null>) =>
      async (): Promise<Float32Array | null> => {
        updateLayer(layerId, { loading: true });
        try {
          const result = await fetchFn();
          if (result === null) updateLayer(layerId, { loading: false });
          return result;
        } catch (e) {
          updateLayer(layerId, { loading: false });
          throw e;
        }
      },
    [updateLayer]
  );

  useEffect(() => {
    const poller = new RealTimePoller();
    pollerRef.current = poller;
    const pushFn = makePushFn();

    // Marker layers start hidden — pollers are registered on-demand when
    // the user toggles them visible (see onToggle).
    const interpolator = new AircraftInterpolator(pushFn);
    interpolatorRef.current = interpolator;

    return () => {
      poller.unregister('earthquakes');
      poller.unregister('fires');
      pollerRef.current = null;
      if (openSkyIntervalRef.current) clearInterval(openSkyIntervalRef.current);
      openSkyIntervalRef.current = null;
      interpolator.stop();
      interpolatorRef.current = null;
    };
  }, [makePushFn, wrapFetch, updateLayer]);

  // --- C++ tile source registration ---
  // Polls until _scRenderer is available, then registers all tile sources.

  useEffect(() => {
    let registered = false;

    const tryRegister = () => {
      if (registered) return;
      const r = (window as any)._scRenderer;
      if (!r || typeof r.addTileSource !== 'function') return;

      registered = true;
      const indices = new Map<string, number>();

      for (const src of TILE_SOURCES) {
        try {
          let url = src.urlTemplate;
          if (url.includes('{date}') && src.id === 'ndvi') {
            const d = new Date();
            d.setDate(d.getDate() - 16);
            url = url.replace('{date}', formatDate(d));
          }

          const idx = r.addTileSource(
            src.name, url, src.gridType, src.maxZoom, src.isOverlay, src.colormapType,
            src.isElevation ?? false
          );
          indices.set(src.id, idx);
          console.log(`[useLayerManager] Registered tile source '${src.id}' index=${idx}`);

          if (!src.defaultVisible) r.setTileSourceVisible(idx, false);
          if (src.defaultOpacity < 1.0) r.setTileSourceOpacity(idx, src.defaultOpacity);
        } catch (e) {
          console.warn(`[useLayerManager] Failed to register tile source '${src.id}':`, e);
        }
      }

      tileIndicesRef.current = indices;
    };

    // Try immediately, then poll every 500ms until renderer is ready
    tryRegister();
    const pollId = setInterval(tryRegister, 500);
    return () => clearInterval(pollId);
  }, []);

  // --- Callbacks ---

  const onToggle = useCallback((id: string, visible: boolean) => {
    const r = (window as any)._scRenderer;

    // Tile source toggle
    const tileIdx = tileIndicesRef.current.get(id);
    if (r && tileIdx !== undefined) {
      try { r.setTileSourceVisible(tileIdx, visible); } catch {}
    }

    // Marker feed toggle — register poller on first enable
    const poller = pollerRef.current;
    if (poller && visible) {
      if (id === 'earthquakes') {
        poller.setLayerVisible(id, true);
        // Register if not already registered (first enable)
        poller.register(id, wrapFetch(id, fetchUSGSEarthquakes), 60_000, makePushFn(), USGS_STRIDE);
      } else if (id === 'fires') {
        poller.setLayerVisible(id, true);
        poller.register(id, wrapFetch(id, fetchFIRMSHotspots), 300_000, makePushFn(), FIRMS_STRIDE);
      } else if (id === 'aircraft') {
        if (!openSkyIntervalRef.current) {
          const interpolator = interpolatorRef.current;
          const pushFn = makePushFn();
          const pollAircraft = async () => {
            try {
              const snapshots = await fetchOpenSkySnapshots(DEFAULT_AIRCRAFT_BBOX);
              if (snapshots !== null && interpolator) {
                interpolator.updateSnapshots(snapshots);
                interpolator.start();
              }
            } catch {}
          };
          pollAircraft();
          openSkyIntervalRef.current = setInterval(pollAircraft, OPENSKY_POLL_MS);
        }
        interpolatorRef.current?.start();
      }
    } else if (poller && !visible) {
      poller.setLayerVisible(id, false);
      if (id === 'aircraft') {
        interpolatorRef.current?.stop();
      }
    }

    // Notify C++ layer manager (for marker overlays)
    if (r && typeof r.setLayerVisible === 'function') {
      try { r.setLayerVisible(id, visible); } catch {}
    }

    updateLayer(id, { visible });
  }, [updateLayer, makePushFn, wrapFetch]);

  const onOpacityChange = useCallback((id: string, opacity: number) => {
    const r = (window as any)._scRenderer;

    const tileIdx = tileIndicesRef.current.get(id);
    if (r && tileIdx !== undefined) {
      try { r.setTileSourceOpacity(tileIdx, opacity); } catch {}
    }

    if (r && typeof r.setLayerOpacity === 'function') {
      try { r.setLayerOpacity(id, opacity); } catch {}
    }

    updateLayer(id, { opacity });
  }, [updateLayer]);

  return { layers, onToggle, onOpacityChange };
}

// ---------- useRasterLayerManager (stub — tile management now in C++) ----------

export function useRasterLayerManager(): null {
  return null;
}

// ---------- Helpers ----------

// Use relative URL so it goes through setupProxy.js (which adds COEP/CORS headers).
// In production, REACT_APP_API_URL is set to the API Gateway URL.
const OPENSKY_PROXY_URL = (process.env.REACT_APP_API_URL || '') + '/api/geo/opensky';

async function fetchOpenSkySnapshots(bbox: AircraftBbox) {
  try {
    const params = new URLSearchParams({
      lamin: String(bbox.latMin), lomin: String(bbox.lonMin),
      lamax: String(bbox.latMax), lomax: String(bbox.lonMax),
    });
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    const resp = await fetch(`${OPENSKY_PROXY_URL}?${params.toString()}`, {
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (resp.status === 503) {
      console.warn('[OpenSky] 503 — credentials not configured, aircraft layer unavailable');
      return null;
    }
    if (!resp.ok) {
      console.warn(`[OpenSky] HTTP ${resp.status}`);
      return null;
    }
    const json = await resp.json();
    if (!json.states || !Array.isArray(json.states)) {
      console.warn('[OpenSky] No states in response');
      return null;
    }
    console.log(`[OpenSky] Got ${json.states.length} aircraft`);
    return parseOpenSkyStates(json.states as unknown[][]);
  } catch (e) {
    console.warn('[OpenSky] Fetch failed:', e);
    return null;
  }
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
